/** A small history router (no router library): `useLocation`, `navigate`, `Link` and path matching. */
import { useSyncExternalStore, type AnchorHTMLAttributes, type MouseEvent } from "react";

const NAV_EVENT = "gx:navigate";

const subscribe = (notify: () => void): (() => void) => {
  window.addEventListener("popstate", notify);
  window.addEventListener(NAV_EVENT, notify);
  return () => { window.removeEventListener("popstate", notify); window.removeEventListener(NAV_EVENT, notify); };
};

export interface Location { readonly pathname: string; readonly search: string }

const snapshot = (): string => window.location.pathname + window.location.search;

/** The current location; re-renders on navigation and on browser back/forward. */
export function useLocation(): Location {
  const href = useSyncExternalStore(subscribe, snapshot);
  const q = href.indexOf("?");
  return q < 0 ? { pathname: href, search: "" } : { pathname: href.slice(0, q), search: href.slice(q) };
}

export function navigate(href: string, opts: { readonly replace?: boolean } = {}): void {
  if (href === snapshot() + window.location.hash) return;
  window.history[opts.replace ? "replaceState" : "pushState"](null, "", href);
  window.dispatchEvent(new Event(NAV_EVENT));
}

export function Link({ href, onClick, ...rest }: AnchorHTMLAttributes<HTMLAnchorElement> & { readonly href: string }) {
  const go = (e: MouseEvent<HTMLAnchorElement>) => {
    onClick?.(e);
    if (e.defaultPrevented || e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || rest.target === "_blank") return;
    e.preventDefault();
    navigate(href);
  };
  return <a href={href} onClick={go} {...rest} />;
}

/** '/c/:cohortId/cockpit' against '/c/abc/cockpit' → { cohortId: 'abc' }; null when it does not match. */
export function matchPath(pattern: string, pathname: string): Readonly<Record<string, string>> | null {
  const want = pattern.split("/");
  const have = pathname.split("/");
  if (want.length !== have.length) return null;
  const params: Record<string, string> = {};
  for (const [i, part] of want.entries()) {
    const seg = have[i] ?? "";
    if (part.startsWith(":")) params[part.slice(1)] = decodeURIComponent(seg);
    else if (part !== seg) return null;
  }
  return params;
}
